import * as React from 'react'

import { cn } from './cn'

type InputElement = HTMLInputElement | HTMLTextAreaElement

export interface TextFieldProps {
  label?: React.ReactNode
  value?: unknown
  defaultValue?: unknown
  onChange?: React.ChangeEventHandler<InputElement>
  onBlur?: React.FocusEventHandler<InputElement>
  onFocus?: React.FocusEventHandler<InputElement>
  onKeyDown?: React.KeyboardEventHandler<InputElement>
  onKeyUp?: React.KeyboardEventHandler<InputElement>
  onClick?: React.MouseEventHandler
  type?: string
  name?: string
  id?: string
  placeholder?: string
  helperText?: React.ReactNode
  error?: boolean
  disabled?: boolean
  required?: boolean
  autoFocus?: boolean
  autoComplete?: string
  fullWidth?: boolean
  multiline?: boolean
  rows?: number
  minRows?: number
  maxRows?: number
  size?: 'small' | 'medium'
  variant?: 'outlined' | 'filled' | 'standard'
  margin?: 'none' | 'dense' | 'normal'
  className?: string
  style?: React.CSSProperties
  /** Attributes for the native input element. */
  inputProps?: Record<string, unknown>
  /** Adornments (start/end) rendered inside the field border. */
  InputProps?: {
    startAdornment?: React.ReactNode
    endAdornment?: React.ReactNode
    className?: string
    readOnly?: boolean
    inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode']
  }
  InputLabelProps?: { shrink?: boolean }
  inputRef?: React.Ref<InputElement>
}

export const TextField = React.forwardRef<HTMLDivElement, TextFieldProps>(
  (
    {
      label,
      helperText,
      error,
      fullWidth,
      multiline,
      rows,
      minRows,
      size = 'medium',
      variant,
      margin,
      className,
      style,
      inputProps,
      InputProps,
      InputLabelProps,
      inputRef,
      id,
      required,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const reactId = React.useId()
    const inputId = id || `ps-field-${reactId}`

    const fieldClasses = cn(
      'w-full min-w-0 grow bg-transparent text-sm text-text-primary outline-none',
      'placeholder:text-text-disabled disabled:text-text-disabled',
      size === 'small' ? 'py-2' : 'py-3',
      multiline && 'resize-y',
    )

    const control = multiline ? (
      <textarea
        id={inputId}
        ref={inputRef as React.Ref<HTMLTextAreaElement>}
        rows={rows ?? minRows ?? 3}
        required={required}
        disabled={disabled}
        readOnly={InputProps?.readOnly}
        className={fieldClasses}
        {...(inputProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
        {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
      />
    ) : (
      <input
        id={inputId}
        ref={inputRef as React.Ref<HTMLInputElement>}
        required={required}
        disabled={disabled}
        readOnly={InputProps?.readOnly}
        className={fieldClasses}
        {...(inputProps as React.InputHTMLAttributes<HTMLInputElement>)}
        {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
      />
    )

    return (
      <div
        ref={ref}
        className={cn(
          'inline-flex flex-col align-top',
          fullWidth && 'w-full',
          margin === 'normal' && 'my-4',
          margin === 'dense' && 'my-2',
          className,
        )}
        style={style}
      >
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'mb-1.5 text-[13px] font-medium',
              error ? 'text-error' : 'text-text-secondary',
            )}
          >
            {label}
            {required && ' *'}
          </label>
        )}
        <div
          className={cn(
            'flex items-center gap-2 rounded-field border bg-paper px-3 shadow-paper',
            'transition-all duration-150',
            'focus-within:border-primary/60 focus-within:shadow-glow-primary',
            error
              ? 'border-error/60'
              : 'border-divider-strong hover:border-text-disabled',
            disabled && 'pointer-events-none opacity-50',
            InputProps?.className,
          )}
        >
          {InputProps?.startAdornment}
          {control}
          {InputProps?.endAdornment}
        </div>
        {helperText && (
          <p
            className={cn(
              'mx-1 mt-1.5 text-xs',
              error ? 'text-error' : 'text-text-secondary',
            )}
          >
            {helperText}
          </p>
        )}
      </div>
    )
  },
)
TextField.displayName = 'TextField'

export default TextField

/** Simple adornment wrapper (old MUI InputAdornment). */
export const InputAdornment = ({
  position,
  className,
  children,
}: {
  position?: 'start' | 'end'
  className?: string
  children?: React.ReactNode
}) => (
  <span
    className={cn('flex shrink-0 items-center text-text-secondary', className)}
    data-position={position}
  >
    {children}
  </span>
)
